import styles from "./mapping-settings.module.css";
import { CSSProperties, useState } from "react";
import { groupBy, prop } from "rambda";
import classNames from "classnames";
import { SetStateAction, useAtomValue } from "jotai";
import { accountAtom } from "state-atoms";
import { NoMappingsIllustration } from "./no-mappings-illustration";
import {
  commitMappingSettingsForGantt,
  MappingMondayToGanttTasks,
  useMappingSettingsForGantt,
} from "frontend/canvas-designer-new/integrations/convert-to-monday/logic/gantt-mapping-hooks/use-mapping-settings-gantt-cards";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import { MondayColumn } from "shared/integrations/mapper";
import { SyncService } from "frontend/services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import {
  Box,
  ExpandableContent,
  FieldMapping,
  HRLight,
  InputFieldsGrid,
  Stack,
} from "../../convert-to-monday/ui/components";
import { TypeOfDateMapping } from "../../convert-to-monday/ui/mappings/gantt-mapping/components-gantt-mapping";
import { disableInvalidOptions } from "./mapping-settings-utils";

type IntegrationsTasksSettingsProps = {
  selectedIntegrationId: string | null;
  documentId?: string;
  syncService: SyncService<RW> | null;
  onDismiss: () => void;
};

// group columns by their type: MondayColumn[] -> {[column-type]: MondayColumn[]}
const groupByType = groupBy<MondayColumn>(prop("type"));

const dateTabKey = "date-tab";
type DateTab = "start-end" | "timeline" | "";

export const IntegrationsMappingSettings = ({
  selectedIntegrationId,
  documentId,
  syncService,
  onDismiss,
}: IntegrationsTasksSettingsProps) => {
  const account = useAtomValue(accountAtom);
  const [isDirty, setIsDirty] = useState(false);
  const { getBoardIntegrationConfig } = useBoardTasks(documentId);
  const config = getBoardIntegrationConfig(selectedIntegrationId!)?.data;

  const [itemsExpanded, setItemsExpanded] = useState(true);
  const [subItemsExpanded, setSubItemsExpanded] = useState(true);

  const boardId = config?.boardId ?? "";
  const [mapping, setMapping, isItemsLoading] = useMappingSettingsForGantt(syncService, boardId);
  const columnsByType = groupByType(config?.columns ?? []);

  const subItemsBoardId = config?.subitemsBoard?.id;
  const [subMapping, setSubMapping, isSubItemsLoading] = useMappingSettingsForGantt(syncService, subItemsBoardId);
  const subItemsColumnsByType = groupByType(config?.subitemsBoard?.columns ?? []);

  const itemDisableDates = !columnsByType["date"]?.length;
  const itemDisableTimeline = !columnsByType["timeline"]?.length;

  const subitemDisableDates = !subItemsColumnsByType["date"]?.length;
  const subitemDisableTimeline = !subItemsColumnsByType["timeline"]?.length;

  const noOptionsAtAll = itemDisableDates && itemDisableTimeline && subitemDisableDates && subitemDisableTimeline;

  const fixedMapping = disableInvalidOptions(dateTabKey, mapping, !itemDisableDates, !itemDisableTimeline);
  const fixedSubMapping = disableInvalidOptions(dateTabKey, subMapping, !subitemDisableDates, !subitemDisableTimeline);

  if (noOptionsAtAll && !isItemsLoading && !isSubItemsLoading) {
    return (
      <>
        <HRLight />
        <div className={styles.noOptionsAtAllWrapper}>
          <NoMappingsIllustration />
          <div className={styles.noOptionsAtAllText}>
            To integrate tasks into the Gantt chart, add Timeline or Date columns to your monday board
          </div>
          <button
            className={classNames(styles.saveButton, styles.goToBoardBTN)}
            onClick={() => {
              window.open(`https://${account?.slug}.monday.com/boards/${boardId}`, "__blank")?.focus();
            }}
          >
            Take me to my board
          </button>
        </div>
      </>
    );
  }

  return (
    <div style={{ overflow: "auto", height: "100%", "--padding-inline": "20px" } as CSSProperties}>
      <HRLight />
      <Box>
        <Stack>
          <div className={styles.mappingTitle}>Map timeline & dependencies</div>
          <div
            className={classNames(styles.mappingSubtitle, { [styles.open]: itemsExpanded })}
            onClick={() => setItemsExpanded(!itemsExpanded)}
          >
            Item columns
          </div>
          <ExpandableContent open={itemsExpanded}>
            {fixedMapping && (
              <>
                <TypeOfDateMapping
                  name="item-date-tab"
                  value={fixedMapping[dateTabKey] as DateTab}
                  onChange={(dateTab) => {
                    setMapping((previous) => Object.assign({}, previous, { [dateTabKey]: dateTab }));
                    setIsDirty(true);
                  }}
                  disableDates={itemDisableDates}
                  disableTimeline={itemDisableTimeline}
                />
                <ColumnsMapping
                  mapping={fixedMapping}
                  setMapping={(...args) => {
                    setMapping(...args);
                    setIsDirty(true);
                  }}
                  columnsByType={columnsByType}
                  idPrefix=""
                />
              </>
            )}
          </ExpandableContent>
        </Stack>
      </Box>
      <HRLight />
      {/* Show the "map items" sub-header only if there are subitems. This is very intentional */}
      {fixedSubMapping && (
        <Box>
          <Stack>
            <div
              className={classNames(styles.mappingSubtitle, { [styles.open]: subItemsExpanded })}
              onClick={() => setSubItemsExpanded(!subItemsExpanded)}
            >
              Subitem columns
            </div>
            <ExpandableContent open={subItemsExpanded}>
              <TypeOfDateMapping
                name="subitem-date-tab"
                value={fixedSubMapping[dateTabKey] as DateTab}
                onChange={(dateTab) => {
                  setSubMapping((previous) => Object.assign({}, previous, { [dateTabKey]: dateTab }));
                  setIsDirty(true);
                }}
                disableDates={subitemDisableDates}
                disableTimeline={subitemDisableTimeline}
              />
              <ColumnsMapping
                mapping={fixedSubMapping}
                setMapping={(...args) => {
                  setSubMapping(...args);
                  setIsDirty(true);
                }}
                columnsByType={subItemsColumnsByType}
                idPrefix="subitem-"
              />
            </ExpandableContent>
          </Stack>
        </Box>
      )}
      <div className={styles.mappingActions}>
        <button className={styles.cancelButton} onClick={onDismiss}>
          Cancel
        </button>
        <button
          className={styles.saveButton}
          disabled={!isDirty}
          onClick={() => {
            if (boardId && fixedMapping) {
              commitMappingSettingsForGantt(syncService, boardId, fixedMapping);
            }
            if (subItemsBoardId && fixedSubMapping) {
              commitMappingSettingsForGantt(syncService, subItemsBoardId, fixedSubMapping);
            }
            onDismiss();
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

const ColumnsMapping = ({
  mapping,
  setMapping,
  columnsByType,
  idPrefix,
}: {
  mapping: MappingMondayToGanttTasks;
  setMapping: React.Dispatch<SetStateAction<MappingMondayToGanttTasks | undefined>>;
  columnsByType: Record<string, { id: string; title: string }[]>;
  idPrefix: string;
}) => (
  <InputFieldsGrid>
    {mapping["date-tab"] === "timeline" ? (
      <FieldMapping
        label={"Timeline"}
        value={mapping.timeline}
        onChange={(id) => setMapping((previous) => Object.assign({}, previous, { timeline: id }))}
        options={columnsByType.timeline}
        testId={`${idPrefix}timeline-select`}
      />
    ) : (
      <>
        <FieldMapping
          label={"Start"}
          value={mapping["start_date"]}
          onChange={(id) => setMapping((previous) => Object.assign({}, previous, { start_date: id }))}
          options={columnsByType.date}
          testId={`${idPrefix}start_date`}
        />
        <FieldMapping
          label={"End"}
          value={mapping["end_date"]}
          onChange={(id) => setMapping((previous) => Object.assign({}, previous, { end_date: id }))}
          options={columnsByType.date}
          testId={`${idPrefix}end_date`}
        />
      </>
    )}

    <FieldMapping
      label={"Dependency"}
      value={mapping.dependency}
      onChange={(id) => setMapping((previous) => Object.assign({}, previous, { dependency: id }))}
      options={columnsByType.dependency}
      testId={`${idPrefix}dependency`}
    />
  </InputFieldsGrid>
);
